/* eslint-disable react/prop-types */
import { Box } from "flicket-ui";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useOrganization } from "~hooks";
import { CreateOrderResultOrder } from "~hooks/reservation/types";
import { useCreateGAOrder } from "../../nonseated.hooks";
import { useNonSeatedReservationStore } from "../../store/non-seated-reservation-store";
import { animation } from "./ExperienceSalesFooter.animation";
import { IExperienceSalesLayout } from "./types";
import { useMaybeGetTicketExchangeData, useFooterActions } from "./hooks";
import { getButtonText, reservationStickyFooter } from "./utils";
import {
  ErrorModal,
  FooterActionBar,
  LockingInPriceFlowModal,
  PriceInfo,
  TicketExchangeSection,
} from "./components";
import { QuantitySetValidationModal } from "~components/reservation/common/common.QuantitySetValidation.modal";
import { isPrechargeFlowAtom } from "~features/nonseated-reservation/isPrechargeFlowAtom";
import { useAtom } from "jotai";
import { addQueryParams, removeQueryParams } from "~lib/helpers/queryParams";
import { CreateOrderOptions } from "~hooks/reservation/useCreateOrder";
import { useStripeConfigContext } from "~lib/stripeConfigContext";
import { OrderOriginatedFrom } from "~graphql/sdk";

const MotionBox = motion(Box);

export const ExperienceSalesFooter = ({
  id,
  releaseId,
  membershipId,
  children,
  timezone,
}: IExperienceSalesLayout) => {
  const router = useRouter();

  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);
  // Used for precharge flow to redirect to checkout after the animation
  const [order, setOrder] = useState<CreateOrderResultOrder | undefined>(
    undefined
  );

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);

  const [showLockingInPriceModal, setShowLockingInPriceModal] = useState(false);

  const { hasFeature, organization } = useOrganization();

  // Get ticket exchange data
  const {
    ownedTicketData,
    hasOwnedTickets: isExchangeFlow,
    ticketsLoading,
    hasTickets,
    ownedTicketsExchangeValue,
  } = useMaybeGetTicketExchangeData(organization?.id);

  const { expressCheckoutEnabled } = useStripeConfigContext();

  // Get state from order creation hook
  const { handleGAOrderCreation } = useCreateGAOrder({
    releaseId,
    membershipId,
    eventId: id,
  });

  const selectedTickets = useNonSeatedReservationStore(
    (state) => state.selectedTicketTypes[id] ?? []
  );

  const cart = useNonSeatedReservationStore((state) => state.totals[id]);

  // Get the global submitting state from the store
  const isStoreSubmitting = useNonSeatedReservationStore(
    (state) => state.isSubmitting
  );

  const hasSelectedTicketsWithPricingTiers = selectedTickets.some(
    (ticket) => ticket.pricingTierId
  );
  const isLockingInPriceFlow =
    isPrechargeFlow || hasSelectedTicketsWithPricingTiers;

  const {
    submitting,
    alertError,
    setAlertError,
    handlePrimaryAction,
    resetState,
  } = useFooterActions({
    handleGAOrderCreation,
    selectedTickets,
    multibuyId: cart?.multibuyId,
    isExchangeFlow,
    skipRedirectToCheckout: isLockingInPriceFlow,
    ownedTicketData,
    onOrderCreated: (order) => {
      setOrder(order);
    },
    onOrderFailed: () => {
      setShowLockingInPriceModal(false);
    },
  });

  const hasSelection = selectedTickets.length > 0;
  const showFees = hasFeature("ShowHiddenFees");

  // Only show footer when either tickets are selected or in exchange flow
  const showFooter =
    !hasFeature("LegacyGATicketSelection") && (hasSelection || isExchangeFlow);

  // Get button text based on current state
  let buttonText = getButtonText(
    isExchangeFlow,
    hasSelection,
    isPrechargeFlow,
    cart?.total ?? 0
  );

  // Hack: Care expos. Remove 2025-12-01
  if (
    [
      "9d87d0e4-4b13-49f9-9cf2-54debf5734a7",
      "9d87cfe3-3465-48fc-8b02-84563086abdc",
      "9d87d30e-d0f7-4687-bb54-5b7570c0b8d0",
    ].includes(organization.id)
  ) {
    buttonText = "Free Ticket";
  }

  const onCloseQuantitySetValidationModal = () => {
    setQuantitySetValidationError(null);
  };

  const validateQuantitySets = (
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    for (const ticketType of selectedTickets) {
      if (!ticketType?.quantitySet) continue;

      const isValid = ticketType.quantity % ticketType.quantitySet === 0;

      if (!isValid) {
        setQuantitySetValidationError({
          name: ticketType.name,
          quantitySet: ticketType.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = (options: CreateOrderOptions = {}) => {
    if (!selectedTickets) return;

    const isValid = validateQuantitySets(setQuantitySetValidationError);

    if (!isValid) return;
    void handlePrimaryAction(options);
  };

  const handleLockingInPriceFlowClose = () => {
    // Reload the page to show updated prices + quantities. Ideally we'd refresh
    // the data instead of reloading, but the page and cart aren't built to
    // support that: mutating the event query doesn't update prices or carts.
    void router.reload();
  };

  if (!cart) {
    return null;
  }

  return (
    <>
      <Box width="100%">
        {children}
        <AnimatePresence>
          {showFooter && (
            <MotionBox
              {...animation}
              position={
                reservationStickyFooter(organization?.id) ? "fixed" : undefined
              }
              zIndex="sticky"
              bottom={0}
              right={0}
              left={0}
              width={1}
              bg="white"
              boxShadow="card"
            >
              <Box
                flex={1}
                px={2}
                py={3}
                maxW="960px"
                mx={"auto" as any}
                minH={80}
              >
                <TicketExchangeSection
                  isExchangeFlow={isExchangeFlow}
                  ticketsLoading={ticketsLoading}
                  hasTickets={hasTickets}
                  ownedTicketData={ownedTicketData}
                  ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                />

                <FooterActionBar
                  priceInfo={
                    <PriceInfo
                      isExchangeFlow={isExchangeFlow}
                      ticketsLoading={ticketsLoading}
                      hasSelection={hasSelection}
                      cart={cart}
                      ownedTicketsExchangeValue={ownedTicketsExchangeValue}
                      showFees={showFees}
                      timezone={timezone}
                    />
                  }
                  buttonText={buttonText}
                  onAction={(options) => {
                    if (options?.isExpressCheckout && !isLockingInPriceFlow) {
                      // Launch the express checkout modal which shows a progress indicator
                      // until the order has been created.
                      addQueryParams(router, {
                        modal: "express-checkout",
                        paymentType: options.paymentType,
                      });
                    }

                    if (isLockingInPriceFlow) {
                      setShowLockingInPriceModal(true);

                      // Add payment method to url and the modal will be launched after
                      // the high demand flow
                      if (options?.paymentType) {
                        addQueryParams(router, {
                          paymentType: options.paymentType,
                        });
                      }
                    }

                    handleBuyButtonClicked(options);
                  }}
                  isLoading={submitting || isStoreSubmitting}
                  isDisabled={
                    !hasSelection ||
                    (isExchangeFlow && ticketsLoading) ||
                    isStoreSubmitting
                  }
                />
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
        <QuantitySetValidationModal
          isOpen={!!quantitySetValidationError}
          onClose={onCloseQuantitySetValidationModal}
          quantitySet={quantitySetValidationError?.quantitySet}
          name={quantitySetValidationError?.name}
        />
      </Box>

      {/* Precharge and pricing tier loading modal. */}
      <LockingInPriceFlowModal
        isOpen={showLockingInPriceModal}
        selectedTickets={selectedTickets}
        order={order}
        showFees={showFees}
        onClose={handleLockingInPriceFlowClose}
        onPriceFlowComplete={(order) => {
          if (
            order.orderOriginatedFrom === OrderOriginatedFrom.ExpressCheckout
          ) {
            setShowLockingInPriceModal(false);

            addQueryParams(router, {
              modal: "express-checkout",
            });
          } else {
            void router.push("/checkout/[orderId]", `/checkout/${order.id}`);
          }

          // Clear order so we don't show old order data if modal is opened again
          setOrder(null);
        }}
      />

      <ErrorModal error={alertError} onClose={() => setAlertError(undefined)} />
    </>
  );
};
